import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Grid,
    Button,
    Chip,
    IconButton,
    Tooltip,
    Avatar,
    Tab,
    Tabs,
    Paper,
    Divider,
    LinearProgress,
    Badge,
    Alert,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Dialog, DialogTitle, DialogContent, DialogActions,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import {
    People as PeopleIcon,
    Security as SecurityIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Add as AddIcon,
    FilterList as FilterIcon,
    MoreVert as MoreIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Badge as BadgeIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Work as WorkIcon,
    AdminPanelSettings as AdminIcon,
    SupervisorAccount as ManagerIcon,
    Engineering as OperatorIcon,
    CheckCircle as ActiveIcon,
    Block as SuspendedIcon,
    TrendingUp as TrendingUpIcon,
    Bolt as ActivityIcon,
    History as HistoryIcon,
    AssignmentTurnedIn as PermissionIcon,
    ViewKanban as KanbanIcon,
    TableChart as TableIcon
} from '@mui/icons-material';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import SmartDataGrid from '../../components/common/SmartDataGrid';
import GlassSidebar from '../../components/common/GlassSidebar';
import KanbanView from '../../components/ui/KanbanView';
import {
    useUsers,
    useUserDashboard,
    useDepartments,
    useRoles,
    useHREmployees,
    useHRMetrics,
    useCreateUser,
    useUpdateUser
} from '../../hooks/useUsers';

/**
 * Pre-configured column definitions for user status Kanban boards.
 * Groups users by access status for workforce management visualization.
 * @constant {Object[]}
 */
const USER_STATUS_KANBAN_COLUMNS = [
    { id: 'active', label: 'Active', color: '#4caf50' },
    { id: 'pending', label: 'Pending', color: '#ff9800' },
    { id: 'inactive', label: 'Inactive', color: '#9e9e9e' },
    { id: 'suspended', label: 'Suspended', color: '#f44336' }
];

/**
 * KPI Card - Aurora 2026 Style
 */
const KPICard = ({ title, value, icon: Icon, trend, color, subtitle, isLoading }) => {
    const isPositive = trend && trend.startsWith('+');

    return (
        <Motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
        >
            <Paper
                sx={{
                    p: 2.5,
                    borderRadius: 4,
                    background: 'rgba(255, 255, 255, 0.03)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '4px',
                        height: '100%',
                        background: color
                    }
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ p: 1, borderRadius: 2, background: `${color}15` }}>
                        <Icon sx={{ color, fontSize: 24 }} />
                    </Box>
                    {trend && (
                        <Chip
                            label={trend}
                            size="small"
                            sx={{
                                bgcolor: isPositive ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                color: isPositive ? '#4caf50' : '#f44336',
                                fontWeight: 700,
                                fontSize: '0.75rem'
                            }}
                        />
                    )}
                </Box>

                {isLoading ? (
                    <Box sx={{ py: 1 }}><LinearProgress color="inherit" sx={{ opacity: 0.1 }} /></Box>
                ) : (
                    <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
                        {value}
                    </Typography>
                )}

                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                    {title}
                </Typography>

                {subtitle && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                        {subtitle}
                    </Typography>
                )}
            </Paper>
        </Motion.div>
    );
};

/**
 * Workforce Cockpit Page - Unified HR & Access Center
 */
const WorkforceCockpitPage = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(0);
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [sidebarTab, setSidebarTab] = useState(0);

    // Data Fetching
    const { data: usersData, isLoading: usersLoading, refetch: refetchUsers } = useUsers({ limit: 100 });
    const { data: dashboardData, isLoading: dashLoading } = useUserDashboard();
    const { data: hrEmployees, isLoading: employeesLoading } = useHREmployees();
    const { data: hrMetrics } = useHRMetrics();
    const { data: roles } = useRoles();
    const { data: departments } = useDepartments();

    const createUserMutation = useCreateUser();
    const updateUserMutation = useUpdateUser();
    
    // Onboarding State
    const [onboardOpen, setOnboardOpen] = useState(false);
    const [onboardForm, setOnboardForm] = useState({
        username: '',
        email: '',
        full_name: '',
        role: 'operator',
        department: 'Operations',
        status: 'active'
    });

    const handleOnboardSubmit = async () => {
        try {
            await createUserMutation.mutateAsync({
                ...onboardForm,
                password: crypto.randomUUID().slice(0, 12) + '!A1'
            });
            setOnboardOpen(false);
            setOnboardForm({ username: '', email: '', full_name: '', role: 'operator', department: 'Operations', status: 'active' });
            refetchUsers();
        } catch (err) {
            console.error("Failed to onboard user", err);
        }
    };

    const handleUpdateUser = async (updates) => {
        if (!selectedUser) return;
        try {
            await updateUserMutation.mutateAsync({
                userId: selectedUser.id,
                data: updates
            });
            // Update local state or refetch
            refetchUsers();
            if (updates.status === 'suspended') {
                 setSidebarOpen(false);
            }
        } catch (err) {
             console.error("Failed to update user", err);
        }
    };


    const users = useMemo(() => usersData?.items || [], [usersData]);
    const employees = useMemo(() => Array.isArray(hrEmployees) ? hrEmployees : [], [hrEmployees]);
    
    // View mode state for System Access tab
    const [viewMode, setViewMode] = useState('table');
    
    // Transform users data for KanbanView
    const kanbanUsers = useMemo(() => {
        return users.map(user => ({
            id: user.id,
            title: user.full_name || user.username,
            subtitle: `${user.role || 'No role'} | ${user.department || 'No dept'}`,
            status: (user.status || 'active').toLowerCase(),
            metadata: {
                email: user.email,
                role: user.role,
                department: user.department,
                last_login: user.last_login
            }
        }));
    }, [users]);
    
    const handleViewModeChange = useCallback((event, newMode) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    }, []);
    
    const handleKanbanStatusChange = useCallback(async (userId, newStatus) => {
        const user = users.find(u => u.id === userId);
        if (user) {
            try {
                await updateUserMutation.mutateAsync({
                    userId: user.id,
                    data: { status: newStatus }
                });
                refetchUsers();
            } catch (err) {
                console.error("Failed to update user status", err);
            }
        }
    }, [users, updateUserMutation, refetchUsers]);

    const handleRowClick = (user) => {
        setSelectedUser(user);
        setSidebarOpen(true);
    };

    const statusColors = {
        active: '#4caf50',
        inactive: '#9e9e9e',
        suspended: '#f44336',
        pending: '#ff9800'
    };

    const getRoleIcon = (role) => {
        const r = (role || '').toLowerCase();
        if (r.includes('admin')) return <AdminIcon sx={{ fontSize: 16 }} />;
        if (r.includes('manager')) return <ManagerIcon sx={{ fontSize: 16 }} />;
        return <OperatorIcon sx={{ fontSize: 16 }} />;
    };

        const userColumns = [
            {
                id: 'identity',
                label: t('common.identity', 'Identity'),
                width: '30%',
                render: (val, row) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                            sx={{
                                width: 40,
                                height: 40,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                fontWeight: 700
                            }}
                        >
                            {row.username ? row.username[0].toUpperCase() : 'U'}
                        </Avatar>
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.full_name || row.username}</Typography>
                            <Typography variant="caption" color="text.secondary">{row.email}</Typography>
                        </Box>
                    </Box>
                )
            },
            {
                id: 'role',
                label: t('common.role', 'Role'),
                render: (val, row) => (
                    <Chip
                        icon={getRoleIcon(row.role)}
                        label={row.role || '-'}
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: 1.5, fontWeight: 600 }}
                    />
                )
            },
            {
                id: 'department',
                label: t('common.department', 'Department'),
                render: (val, row) => row.department || '-'
            },
            {
                id: 'last_login',
                label: t('common.lastActivity', 'Last Activity'),
                render: (val, row) => row.last_login ? new Date(row.last_login).toLocaleDateString() : t('common.never', 'Never')
            },
            {
                id: 'status',
                label: t('common.status', 'Status'),
            render: (val, row) => {
                const status = (row.status || 'active').toLowerCase();
                return (
                    <Chip
                        label={status.toUpperCase()}
                        size="small"
                        sx={{
                            bgcolor: `${statusColors[status]}15`,
                            color: statusColors[status],
                            fontWeight: 800,
                            fontSize: '0.65rem'
                        }}
                    />
                );
            }
        }
    ];


    const orgColumns = [
        { id: 'name', label: t('common.workforce.departmentName', 'Department Name'), flex: 1 },
        { id: 'manager', label: t('common.workforce.headOfDept', 'Head of Dept'), render: (val) => val || '-' },
        { id: 'headcount', label: t('common.workforce.headcount', 'Headcount'), render: (val, row) => row.employee_count || 0 },
        { id: 'budget', label: t('common.workforce.budgetCode', 'Budget Code'), render: (val, row) => row.budget_code || '-' }
    ];

    const employeeColumns = [
        {
            id: 'staff',
            label: t('common.workforce.staffMember', 'Staff Member'),
            width: '30%',
            render: (val, row) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar src={row.avatar_url} sx={{ width: 40, height: 40 }} />
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.first_name} {row.last_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.employee_id || '-'}</Typography>
                    </Box>
                </Box>
            )
        },
        {
            id: 'position',
            label: t('common.workforce.position', 'Position'),
            render: (val, row) => row.position || '-'
        },
        {
            id: 'department',
            label: t('common.workforce.department', 'Department'),
            render: (val, row) => row.department_name || '-'
        },
        {
            id: 'hire_date',
            label: t('common.workforce.hired', 'Hired'),
            render: (val, row) => row.hire_date || '-'
        }
    ];

    return (
        <Box sx={{ p: 3, position: 'relative' }}>
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
                <Box>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 800,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            letterSpacing: '-0.03em',
                            mb: 0.5
                        }}
                    >
                        {t('common.workforce.cockpitTitle', 'Workforce Cockpit')}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {t('common.workforce.cockpitSubtitle', 'Orchestration center for staff profiles, system access, and organizational telemetry.')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        startIcon={<HistoryIcon />}
                        sx={{ borderRadius: 2.5, px: 2, height: 42 }}
                    >
                        {t('common.workforce.auditLogs', 'Audit Logs')}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOnboardOpen(true)}
                        sx={{
                            borderRadius: 2.5,
                            px: 3,
                            height: 42,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            boxShadow: '0 8px 20px -6px rgba(118, 75, 162, 0.5)'
                        }}
                    >
                        {t('common.workforce.onboardStaff', 'Onboard Staff')}
                    </Button>
                </Box>
            </Box>

            {/* KPI Grid */}
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title={t('common.workforce.activeOperators', 'Active Operators')}
                        value={dashboardData?.activeUsers || 0}
                        icon={ActivityIcon}
                        trend="+12%"
                        color="#4caf50"
                        isLoading={dashLoading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title={t('common.workforce.accessCoverage', 'Access Coverage')}
                        value={`${hrMetrics?.access_coverage ?? hrMetrics?.mfa_coverage ?? 0}%`}
                        icon={SecurityIcon}
                        trend="+0.5%"
                        color="#2196f3"
                        subtitle={t('common.workforce.mfaEnabled', 'Accounts with MFA enabled')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title={t('common.workforce.retentionRate', 'Retention Rate')}
                        value={`${hrMetrics?.retention_rate || 0}%`}
                        icon={TrendingUpIcon}
                        trend="+2.1%"
                        color="#f093fb"
                        subtitle={t('common.workforce.staffStability', 'Annualized staff stability')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title={t('common.workforce.roleCapacity', 'System Role Capacity')}
                        value={`${hrMetrics?.role_capacity ?? hrMetrics?.license_utilization ?? '—'}%`}
                        icon={BadgeIcon}
                        color="#ff9800"
                        subtitle={t('common.workforce.licenseUtilization', 'License utilization')}
                    />
                </Grid>
            </Grid>

            {/* Main Content Area */}
            <Paper
                sx={{
                    borderRadius: 4,
                    background: 'rgba(255, 255, 255, 0.02)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    overflow: 'hidden'
                }}
            >
                <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{
                            px: 2,
                            '& .MuiTab-root': {
                                minHeight: 64,
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                textTransform: 'none',
                            }
                        }}
                    >
                        <Tab icon={<PeopleIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('common.workforce.staffDirectory', 'Staff Directory')} />
                        <Tab icon={<SecurityIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('common.workforce.systemAccess', 'System Access')} />
                        <Tab icon={<WorkIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('common.workforce.orgStructure', 'Org Structure')} />
                    </Tabs>
                </Box>

                <Box sx={{ p: 0 }}>
                    <AnimatePresence mode="wait">
                        {activeTab === 0 && (
                            <Motion.div
                                key="tab-0"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 10 }}
                                transition={{ duration: 0.2 }}
                            >
                                <SmartDataGrid
                                    columns={employeeColumns}
                                    data={employees}
                                    loading={employeesLoading}
                                    onRowClick={handleRowClick}
                                />
                            </Motion.div>
                        )}

                        {activeTab === 1 && (
                            <Motion.div
                                key="tab-1"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 10 }}
                                transition={{ duration: 0.2 }}
                            >
                                <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <ToggleButtonGroup
                                            value={viewMode}
                                            exclusive
                                            onChange={handleViewModeChange}
                                            size="small"
                                        >
                                            <ToggleButton value="table">
                                                <Tooltip title={t('common.tableView', 'Table View')}>
                                                    <TableIcon />
                                                </Tooltip>
                                            </ToggleButton>
                                            <ToggleButton value="kanban">
                                                <Tooltip title={t('common.kanbanView', 'Kanban View')}>
                                                    <KanbanIcon />
                                                </Tooltip>
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                    <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                                        {viewMode === 'table' ? (
                                            <SmartDataGrid
                                                columns={userColumns}
                                                data={users}
                                                loading={usersLoading}
                                                onRowClick={handleRowClick}
                                                quickFilters={[
                                                    { key: 'active', label: t('common.active', 'Active'), color: 'success' },
                                                    { key: 'suspended', label: t('common.suspended', 'Suspended'), color: 'error' }
                                                ]}
                                            />
                                        ) : (
                                            <KanbanView
                                                items={kanbanUsers}
                                                columns={USER_STATUS_KANBAN_COLUMNS}
                                                onStatusChange={handleKanbanStatusChange}
                                                onItemClick={(item) => {
                                                    const user = users.find(u => u.id === item.id);
                                                    if (user) {
                                                        setSelectedUser(user);
                                                        setSidebarOpen(true);
                                                    }
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Motion.div>
                        )}

                        {activeTab === 2 && (
                            <Motion.div
                                key="tab-2"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 10 }}
                                transition={{ duration: 0.2 }}
                            >
                                <SmartDataGrid
                                    columns={orgColumns}
                                    data={departments || []}
                                    loading={false}
                                />
                            </Motion.div>
                        )}
                    </AnimatePresence>
                </Box>
            </Paper>

            {/* Integrated Profile & Access Sidebar */}

            <Dialog open={onboardOpen} onClose={() => setOnboardOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>{t('common.workforce.onboardNewStaff', 'Onboard New Staff Member')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                        <TextField
                            label={t('common.workforce.fullName', 'Full Name')}
                            fullWidth
                            value={onboardForm.full_name}
                            onChange={(e) => setOnboardForm({ ...onboardForm, full_name: e.target.value })}
                        />
                        <TextField
                            label={t('common.workforce.username', 'Username')}
                            fullWidth
                            value={onboardForm.username}
                            onChange={(e) => setOnboardForm({ ...onboardForm, username: e.target.value })}
                        />
                         <TextField
                            label={t('common.workforce.emailAddress', 'Email Address')}
                            fullWidth
                            type="email"
                            value={onboardForm.email}
                            onChange={(e) => setOnboardForm({ ...onboardForm, email: e.target.value })}
                        />
                        <FormControl fullWidth>
                            <InputLabel>{t('common.role', 'Role')}</InputLabel>
                            <Select
                                value={onboardForm.role}
                                label={t('common.role', 'Role')}
                                onChange={(e) => setOnboardForm({ ...onboardForm, role: e.target.value })}
                            >
                                {roles?.map(r => <MenuItem key={r.id} value={r.name}>{r.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>{t('common.workforce.department', 'Department')}</InputLabel>
                            <Select
                                value={onboardForm.department}
                                label={t('common.workforce.department', 'Department')}
                                onChange={(e) => setOnboardForm({ ...onboardForm, department: e.target.value })}
                            >
                                {departments?.map(d => <MenuItem key={d.id} value={d.name}>{d.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOnboardOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
                    <Button
                        variant="contained"
                        onClick={handleOnboardSubmit}
                        disabled={!onboardForm.username || createUserMutation.isPending}
                        sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                    >
                        {createUserMutation.isPending ? t('common.workforce.provisioning', 'Provisioning...') : t('common.workforce.provisionAccess', 'Provision Access')}
                    </Button>
                </DialogActions>
            </Dialog>

            <GlassSidebar
                isOpen={isSidebarOpen}
                onClose={() => setSidebarOpen(false)}
                title={selectedUser?.full_name || selectedUser?.username || "-"}
            >
                <Box sx={{ p: 3 }}>
                    <Tabs value={sidebarTab} onChange={(e, v) => setSidebarTab(v)} variant="fullWidth" sx={{ mb: 3 }}>
                        <Tab label={t('common.profileLabel', 'Profile')} />
                        <Tab label={t('common.security', 'Security')} />
                        <Tab label={t('common.workforce.hr', 'HR')} />
                    </Tabs>

                    <AnimatePresence mode="wait">
                        {sidebarTab === 0 && (
                            <Motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="st-0">
                                <Box sx={{ mb: 3, textAlign: 'center' }}>
                                    <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, background: 'primary.main' }}>
                                        {selectedUser?.username?.[0].toUpperCase()}
                                    </Avatar>
                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{selectedUser?.full_name}</Typography>
                                    <Typography variant="body2" color="text.secondary">{selectedUser?.role}</Typography>
                                </Box>

                                <Stack spacing={2}>
                                    <TextField fullWidth label={t('common.email', 'Email')} value={selectedUser?.email || ''} variant="outlined" size="small" />
                                    <TextField fullWidth label={t('common.workforce.username', 'Username')} value={selectedUser?.username || ''} variant="outlined" size="small" />
                                    <Button variant="contained" fullWidth onClick={() => handleUpdateUser({ username: selectedUser.username, email: selectedUser.email })}>{t('common.applyChanges', 'Apply Changes')}</Button>
                                </Stack>
                            </Motion.div>
                        )}

                        {sidebarTab === 1 && (
                            <Motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="st-1">
                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>{t('common.workforce.rbacConfig', 'RBAC Configuration')}</Typography>
                                <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                                    <InputLabel>{t('common.workforce.systemRole', 'System Role')}</InputLabel>
                                    <Select value={selectedUser?.role || 'operator'} label={t('common.workforce.systemRole', 'System Role')}>
                                        {roles?.map(r => <MenuItem key={r.id} value={r.name}>{r.name}</MenuItem>)}
                                    </Select>
                                </FormControl>

                                <Alert severity="info" sx={{ mb: 3 }}>
                                    {t('common.workforce.effectivePermissions', 'This user has 14 effective permissions inherited from the [Operator] role.')}
                                </Alert>

                                <Button variant="outlined" fullWidth color="error" startIcon={<SuspendedIcon />} onClick={() => handleUpdateUser({ status: "suspended" })}>{t('common.workforce.suspendAccess', 'Suspend Access')}</Button>
                            </Motion.div>
                        )}

                        {sidebarTab === 2 && (
                            <Motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="st-2">
                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>{t('common.workforce.logisticsContext', 'Logistics Context')}</Typography>
                                <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)', mb: 3 }}>
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">{t('common.workforce.shift', 'Shift')}</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('common.workforce.dayShiftA', 'Day Shift (A)')}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">{t('common.workforce.zone', 'Zone')}</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>Pick-Zone-Alpha</Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                                <Button variant="outlined" fullWidth startIcon={<BadgeIcon />}>{t('common.workforce.viewHrFolder', 'View HR Folder')}</Button>
                            </Motion.div>
                        )}
                    </AnimatePresence>
                </Box>
            </GlassSidebar>
        </Box>
    );
};

export default React.memo(WorkforceCockpitPage);
